import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { isEmpty } from 'lodash';
import { hasSpecialCharacters, formatCampaignName } from '../../../utils/campaignNaming';
import { determineUserAccess } from '../../../permissions/userLevelAccess';
import {
  computeSpend,
  formatCurrency2SigFig,
  formatWholeNumberNaN,
} from '../../../utils/campaigns';
import { defaultCPM } from '../../../utils/constants/constants';
import { compute } from '../../../../platform/ux/Home/utils/computeWeeklyReport';
import { prepareCSV } from '../../../../platform/ux/Home/utils/weeklyReportCSV';

const DisplayAnalyticsController = ({ children, data, startDate, endDate, orgs, ...props }) => {
  const [byDate, setByDate] = useState([]);
  const [byDay, setByDay] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [campaignOrders, setCampaignOrders] = useState([]);
  const [selectedCOIds, setSelectedCOIds] = useState([]);
  const [rawCampaignOrders, setRawCampaignOrders] = useState([]);

  const userPermission = determineUserAccess(props.currentUser.permission);
  const buildCharts = useCallback(
    (_campaignOrders) => {
      const consolidatedByDate = {};
      const consolidatedByDay = {};
      const allTableData = [];
      const displayCampaignOrders = [];
      for (const campaign of _campaignOrders) {
        displayCampaignOrders.push({
          id: campaign.id,
          name: !hasSpecialCharacters(campaign.name) ? formatCampaignName(campaign) : campaign.name,
        });
        let summaryData = campaign.aggregateData?.completeAttributionSummary;
        if (summaryData) {
          try {
            if (summaryData && Array.isArray(summaryData)) {
              if (startDate && endDate) {
                summaryData = summaryData.filter((dateEntry) => {
                  return (
                    moment(dateEntry.xAxis, 'YYYY-MM-DD').isSameOrAfter(
                      moment(startDate, 'YYYY-MM-DD')
                    ) &&
                    moment(dateEntry.xAxis, 'YYYY-MM-DD').isSameOrBefore(
                      moment(endDate, 'YYYY-MM-DD')
                    )
                  );
                });
              }

              summaryData.forEach((dateEntry) => {
                const dateKey = dateEntry.xAxis;
                const dayKey = moment(dateEntry.xAxis, 'YYYY-MM-DD').format('dddd');
                const clicks = Number(dateEntry.clicks) || 0;
                const impressions = Number(dateEntry.impressions) || 0;
                const transactionCount = Number(dateEntry.transactionCount) || 0;
                const transactionTotal = Number(dateEntry.transactionTotal) || 0;
                const totalImpressions = Number(dateEntry.totalImpressions) || 0;
                const walkIns = Number(dateEntry.walkIns) || 0;
                const observations = Number(dateEntry.observations) || 0;
                const signUps = Number(dateEntry.signUps) || 0;
                const userCost = Number(dateEntry.userCost) || 0;
                const sessions = Number(dateEntry.sessions) || 0;

                // Consolidate by date
                if (dateKey) {
                  if (!consolidatedByDate[dateKey]) {
                    consolidatedByDate[dateKey] = {
                      clicks: 0,
                      ctr: 0,
                      impressions: 0,
                      roas: 0,
                      transactionCount: 0,
                      transactionTotal: 0,
                      totalImpressions: 0,
                      walkIns: 0,
                      observations: 0,
                      signUps: 0,
                      userCost: 0,
                      sessions: 0,
                      xAxis: dateKey,
                    };
                  }

                  consolidatedByDate[dateKey].clicks += clicks;
                  consolidatedByDate[dateKey].impressions += impressions;
                  consolidatedByDate[dateKey].transactionCount += transactionCount;
                  consolidatedByDate[dateKey].transactionTotal += transactionTotal;
                  consolidatedByDate[dateKey].totalImpressions += totalImpressions;
                  consolidatedByDate[dateKey].walkIns += walkIns;
                  consolidatedByDate[dateKey].observations += observations;
                  consolidatedByDate[dateKey].signUps += signUps;
                  consolidatedByDate[dateKey].userCost += userCost;
                  consolidatedByDate[dateKey].sessions += sessions;
                }

                // Consolidate by day
                if (dayKey) {
                  if (!consolidatedByDay[dayKey]) {
                    consolidatedByDay[dayKey] = {
                      clicks: 0,
                      ctr: 0,
                      impressions: 0,
                      roas: 0,
                      transactionCount: 0,
                      transactionTotal: 0,
                      totalImpressions: 0,
                      walkIns: 0,
                      observations: 0,
                      signUps: 0,
                      userCost: 0,
                      sessions: 0,
                      xAxis: dayKey,
                    };
                  }

                  consolidatedByDay[dayKey].clicks += clicks;
                  consolidatedByDay[dayKey].impressions += impressions;
                  consolidatedByDay[dayKey].transactionCount += transactionCount;
                  consolidatedByDay[dayKey].transactionTotal += transactionTotal;
                  consolidatedByDay[dayKey].totalImpressions += totalImpressions;
                  consolidatedByDay[dayKey].walkIns += walkIns;
                  consolidatedByDay[dayKey].observations += observations;
                  consolidatedByDay[dayKey].signUps += signUps;
                  consolidatedByDay[dayKey].userCost += userCost;
                  consolidatedByDay[dayKey].sessions += sessions;
                }

                // Add to table data
                allTableData.push({
                  id: campaign.id,
                  date: dateKey,
                  name: !hasSpecialCharacters(campaign.name)
                    ? formatCampaignName(campaign)
                    : campaign.name,
                  impressions,
                  clicks,
                  ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
                  transactions: transactionCount,
                  revenue: transactionTotal,
                  spend: userCost,
                  signups: signUps,
                  sessions,
                });
              });
            } else {
              console.log('No byDate array found in summary data');
            }
          } catch (parseError) {
            console.log('Failed to parse completeAttributionSummary:', parseError);
          }
        } else {
          console.log('No completeAttributionSummary found');
        }
      }

      // Calculate CTR and ROAS for consolidated data by date
      const finalConsolidatedByDate = Object.values(consolidatedByDate).map((entry) => {
        entry.ctr = entry.impressions > 0 ? (entry.clicks / entry.impressions) * 100 : 0;
        entry.roas = entry.userCost > 0 ? entry.transactionTotal / entry.userCost : 0;
        return entry;
      });

      // Calculate CTR and ROAS for consolidated data by day
      const dayOrder = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ];
      const finalConsolidatedByDay = dayOrder
        .filter((day) => consolidatedByDay[day])
        .map((day) => {
          const entry = consolidatedByDay[day];
          entry.ctr = entry.impressions > 0 ? (entry.clicks / entry.impressions) * 100 : 0;
          entry.roas = entry.userCost > 0 ? entry.transactionTotal / entry.userCost : 0;
          return entry;
        });

      setByDate(finalConsolidatedByDate);
      setByDay(finalConsolidatedByDay);
      setTableData(allTableData);
      setCampaignOrders(displayCampaignOrders);
      setRawCampaignOrders(_campaignOrders);
    },
    [startDate, endDate]
  );

  useEffect(() => {
    if (data && data.campaignOrders) {
      let campaignOrders = data.campaignOrders;
      if (selectedCOIds.length > 0) {
        campaignOrders = campaignOrders.filter((campaignOrder) => {
          return selectedCOIds.includes(campaignOrder.id);
        });
      }
      buildCharts(campaignOrders);
    }
  }, [buildCharts, data, selectedCOIds]);

  const prepareExport = (type = 'csv') => {
    // default to csv
    const zip = new JSZip();
    const folder = zip.folder('ReportsCampaign');

    let reportToExport = tableData.slice(0);
    reportToExport = reportToExport.sort((a, b) => a.date - b.date);

    // delete data today if range date is default/empty
    // this only applies for advertiser weekly performance
    if (isEmpty(byDate)) {
      reportToExport.pop();
    }

    let impressions = 0,
      clicks = 0,
      transactions = 0,
      revenue = 0,
      sessions = 0,
      signups = 0,
      spend = 0;

    const copy = reportToExport.map((row) => {
      const r = { ...row };
      const orgs = r.orgs ? r.orgs.map((o) => o.name) : [];
      r.Organizations = orgs.toString();
      delete r.orgs;
      // avoid empty cells in the export
      for (const key in r) {
        if (r[key] === null) {
          r[key] = 0;
        }
      }
      impressions += r.impressions || 0;
      clicks += r.clicks || 0;
      transactions += r.transactionCount || 0;
      revenue += r.transactionTotal || 0;
      sessions += r.sessions || 0;
      signups += r.signups || 0;

      const calculateSpend = userPermission.priceVisible
        ? computeSpend(r.impressions, r.cpm || defaultCPM.cpm)
        : 0;
      spend += calculateSpend;

      const objToReturn = {
        DATE: r.date,
        'ORDER ID': r.id,
        'CAMPAIGN ORDER NAME': r.name,
        IMPRESSIONS: r.impressions,
        CLICKS: r.clicks,
        CTR: `${r.ctr.toFixed(3) || 0}%`,
        TRANSACTIONS: r.transactions,
        REVENUE: formatCurrency2SigFig(r.revenue),
        SPEND: formatCurrency2SigFig(r.spend),
        SIGNUPS: formatWholeNumberNaN(r.signups),
        SESSIONS: formatWholeNumberNaN(r.sessions),
        ORGS: orgs && orgs.length ? JSON.stringify(orgs) : '',
      };

      if (!userPermission.priceVisible) {
        delete objToReturn['SPEND'];
      }
      return objToReturn;
    });

    const totalObjToReturn = {
      DATE: 'Totals',
      'ORDER ID': '',
      'CAMPAIGN ORDER NAME': '',
      IMPRESSIONS: impressions,
      CLICKS: clicks,
      CTR: `${((clicks / impressions) * 100).toFixed(2)}%`,
      TRANSACTIONS: transactions,
      REVENUE: !isNaN(revenue) ? revenue.toFixed(3) : 0,
      SPEND: !isNaN(spend) ? spend.toFixed(3) : 0,
      SIGNUPS: formatWholeNumberNaN(signups),
      SESSIONS: formatWholeNumberNaN(sessions),
      ORGS: [],
    };

    if (!userPermission.priceVisible) {
      delete totalObjToReturn['SPEND'];
    }
    copy.push(totalObjToReturn);

    switch (type) {
      case 'xlsx':
        const fileType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const ws = XLSX.utils.json_to_sheet(copy);
        const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        folder.file(`PerformanceReport.xlsx`, data);
        break;
      default:
        // default is csv
        folder.file(`PerformanceReport.csv`, Papa.unparse(copy));
    }

    zip.generateAsync({ type: 'blob' }).then(
      (blob) => {
        saveAs(blob, 'Performance Report');
        // setIsExportCompleted(true);
        return true;
      },
      function (e) {
        console.log('error', e);
        return false;
      }
    );
  };

  const onExportWeeklyReport = async (type = 'csv') => {
    // default to csv
    // only affected by campaign order filter and not by date
    const zip = new JSZip();
    const folder = zip.folder('Weekly Report');
    const prepareData = prepareCSV(compute(tableData));

    switch (type) {
      case 'xlsx':
        const fileType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const ws = XLSX.utils.json_to_sheet(prepareData);
        const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        folder.file(`WeeklyReport.xlsx`, data);
        console.log('yea');
        break;
      default:
        // default is csv
        folder.file(`WeeklyReport.csv`, Papa.unparse(prepareData));
    }

    zip.generateAsync({ type: 'blob' }).then(
      (blob) => {
        saveAs(blob, 'Weekly Report');
        return true;
      },
      function (e) {
        console.log('error', e);
        return false;
      }
    );
  };

  return React.cloneElement(children, {
    ...props,
    byDate,
    byDay,
    tableData,
    userPermission,
    prepareExport,
    orgs,
    campaignOrders,
    rawCampaignOrders,
    selectedCOIds,
    setSelectedCOIds,
    onExportWeeklyReport,
  });
};

export default DisplayAnalyticsController;
